import {
  Component,
  Element,
  Fragment,
  h,
  Host,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import { zapierURL } from "../../api/config";
import {
  createUTMObject,
  eventNames,
  formatEmbedConfiguration,
  UTMObject,
} from "../../api/events";
import {
  fetchApp,
  fetchSlugAndSelectedAPI,
  fetchZapTemplates,
  fetchZapTemplatesByIds,
  getImageUrlForSelectedApi,
  guessZap,
  sendEmbedViewEvent,
  ZapGuess,
} from "../../api/repository";
import { App, EmbedViewEvent, QuacInfo, ZapSuggestion } from "../../api/types";
import { ImageZapEmptyState } from "../../lib/images";
import { getAnchorTarget } from "../../lib/link-target";
import { Logger } from "../../lib/logger";
import { mediaColorSchemeQuery } from "../../lib/mediaQueries";
import { parseCsvSlugs } from "../../lib/propParsing";
import { rootElementMetadata } from "../../lib/root-element-metadata";
import { referrerWithUTMs } from "../../lib/referrer";
import {
  localStorageGetWithFallback,
  localStorageSet,
} from "../../lib/storage";
import { InternalDemoMode } from "../../lib/manage-zaps";
import { ModalOpen } from "../../lib/modal";
import { waitForPopupClose } from "../zapier-internal-manage-zaps-iframe/utils";
import * as WFE from "../../lib/workflow-element";
import { browser } from "../../lib/userAgent";
import { assertNeverSoftly } from "../../lib/assertNever";
import { hasValidQuacParams } from "../../lib/quac";
import { CloseIcon, EvergreenAiIcon, WarningIcon } from "../../lib/icons";

const html = String.raw;

// It's worth noting that the App Directory Element (ADE), Zap Manager Element
// (ZME), Zap Templates Element (ZTE), and Full Zapier Experience (FZE) can all
// be "auto upgraded" to this element. In the case of the ADE/ZME/ZTE, not all
// features are possible. This component was originally written under the
// assumption that there would always be a client ID, so some things are a
// little messy now that it's not true any more. The ADE and the ZTE never
// required a client ID, so this code will lack it in many scenarios.
@Component({
  tag: "zapier-workflow",
  styleUrl: "zapier-workflow.css",
  shadow: true,
})
export class ZapierWorkflow {
  // ---------------------------------------------------------------------------
  // Required props
  // ---------------------------------------------------------------------------

  /** (REQUIRED) Client ID used to identify your Zapier app */
  @Prop({ reflect: true }) clientId = "";

  // ---------------------------------------------------------------------------
  // General props
  // ---------------------------------------------------------------------------

  /**
   * For the purpose of upgrading the App Directory Element (ADE) and Zap
   * Templates Element (ZTE), we support a limited form of using the Workflow
   * Element (WFE) without using a client ID. This is a comma separated string
   * list of apps.
   */
  @Prop({ reflect: false }) internalApp = "";

  /**
   * When automatically upgrading the ADE/ZTE/ZME/FZE, let's take note of which
   * element was being used originally. This is a signal for what sorts of
   * legacy behaviors we need to account for. This is mostly used for
   * automatically hiding UI elements that aren't wanted.
   */
  @Prop({ reflect: false }) internalAutoUpgradedFrom?: WFE.AutoUpgradeSource;

  /**
   * Enable dark mode, or auto to go between light and dark modes depending on
   * the user's client setting
   */
  @Prop({ reflect: true }) theme: WFE.Theme | "auto" = "light";

  /**
   * Define if embed will open on a popup or iframe. Currently, default to
   * iframe since auto mode is not supported yet. It'll be implemented on
   * TPS-3086.
   */
  @Prop({ reflect: true }) embedMode: WFE.EmbedMode | "auto" = "auto";

  // ---------------------------------------------------------------------------
  // Intro props
  // ---------------------------------------------------------------------------

  /** The intro copy display mode, which may currently be set to 'hide' */
  @Prop({ reflect: true }) introCopyDisplay: WFE.DisplayMode = "show";

  // ---------------------------------------------------------------------------
  // App selection props
  // ---------------------------------------------------------------------------

  /** Show the app search area */
  @Prop({ reflect: true }) appSelectionDisplay: WFE.DisplayMode = "show";

  /** The searchbar display mode, which may currently be set to 'hide' */
  @Prop({ reflect: true }) appSearchBarDisplay: WFE.DisplayMode = "show";

  /** The number of apps to display */
  @Prop({ reflect: true }) appLimit = 0;

  /** A comma separated list of apps to exclude */
  @Prop({ reflect: true }) appExclusions = "";

  /**
   * A comma separated list of categories to include or exclude (prefix with `-`
   * to exclude)
   */
  @Prop({ reflect: true }) appCategories = "";

  // ---------------------------------------------------------------------------
  // Manage zaps props
  // ---------------------------------------------------------------------------

  /** This will hide the manage zaps if set to 'hide' */
  @Prop({ reflect: true }) manageZapsDisplay: WFE.DisplayMode = "show";

  // ---------------------------------------------------------------------------
  // Zap guesser props
  // ---------------------------------------------------------------------------

  /** This will hide the manage zaps if set to 'hide' */
  @Prop({ reflect: true }) guessZapDisplay: WFE.DisplayMode = "show";

  // ---------------------------------------------------------------------------
  // Zap templates props
  // ---------------------------------------------------------------------------

  /** Comma separated string of Zap Template IDs */
  @Prop({ reflect: true }) templateIds = "";

  /** The number of premade Zaps to display */
  @Prop({ reflect: true }) templateLimit = 5;

  /** This will hide the button on the Zap Row if set to true */
  @Prop({ reflect: true }) templateCtaDisplay: WFE.DisplayMode = "show";

  /**
   * How/whether to present the "create [Zap] from scratch" prompt, which leads
   * the user to create a Zap without a template.
   */
  @Prop({ reflect: true }) zapCreateFromScratchDisplay: WFE.DisplayMode =
    "hide";

  /**
   * Optional value for the `fb_embed` query parameter for Zap Tempaltes Element
   * (ZTE) backwards compatibility.
   */
  @Prop({ reflect: false }) internalFacebookEmbed = "";

  // ---------------------------------------------------------------------------
  // Quick Account Creation (QuAC) Props
  // ---------------------------------------------------------------------------

  /** The suggested first name for the user */
  @Prop({ reflect: true }) signUpFirstName = "";

  /** The suggested last name for the user */
  @Prop({ reflect: true }) signUpLastName = "";

  /** The suggested email for the user */
  @Prop({ reflect: true }) signUpEmail = "";

  // ---------------------------------------------------------------------------
  // Elements
  // ---------------------------------------------------------------------------

  @Element() private elem!: HTMLZapierWorkflowElement;
  private manageZapsElement?: HTMLZapierInternalManageZapsElement;

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  /**
   * The main app (e.g. "google-sheets",  "any-do", "trello")
   */
  @State() private appSlug = "";
  /**
   * When upgrading the ZTE to the WFE, more than one app may be supplied.
   * Unfortunately the backend "ANDs" these together, looking for Zap Templates
   * that have _every_ app selected. This is generally not useful to partners,
   * since any more than 2 apps is extremely unlikely to return any results.
   * Ideally we'd have a "main app" that's required, and every other app is a
   * possibility in the list. But the backend doesn't support that right now.
   */
  @State() private otherAppSlugs: string[] = [];
  @State() private selectedAPI = "";

  @State() private isLoggedIn = false;

  /** Is the Zap modal open? And what URL should it have. */
  @State() private modalState: WFE.ModalState = { type: "closed" };

  /**
   * This is the computed theme state. It's never set to "auto", unlike the
   * theme parameter.
   */
  @State() private themeState: "dark" | "light" = "light";

  @State() private firstApp?: App;
  @State() private secondApp?: App;
  @State() private loadingSuggestions = false;
  @State() private suggestions: ZapSuggestion[] = [];
  @State() private zapGuesserPrompt = "";
  @State() private zapGuesserAbortController = new AbortController();
  @State() private zapGuesserIsLoading = false;
  @State() private zapGuesserError?: Error;
  @State() private zapGuesserResult?: ZapGuess;

  /**
   * The current app selection. This probably shouldn't be a `number | App`, but
   * we haven't taken the time to rewrite the code to make more sense.
   */
  @State() selection: number | App = -1;

  // ---------------------------------------------------------------------------
  // Methods
  // ---------------------------------------------------------------------------

  private _setCalculatedTheme = () => {
    if (this.theme === "auto") {
      this.themeState = mediaColorSchemeQuery.matches ? "dark" : "light";
    } else {
      this.themeState = this.theme;
    }
  };

  private get internalDisableAAC(): boolean {
    return "internalDisableAac" in this.elem.dataset;
  }

  private get isDemoMode(): InternalDemoMode {
    return this.elem.dataset.internalDemoMode as InternalDemoMode;
  }

  private get internalTestIframeBlocked(): boolean {
    return "internalTestIframeBlocked" in this.elem.dataset;
  }

  @Watch("isLoggedIn")
  saveLogInState() {
    localStorageSet("is_logged_in", this.isLoggedIn);
  }

  @Watch("theme")
  appThemeChanged() {
    this._setCalculatedTheme();
  }

  connectedCallback() {
    this._setCalculatedTheme();
    mediaColorSchemeQuery.addEventListener("change", this._setCalculatedTheme);
  }

  disconnectedCallback() {
    mediaColorSchemeQuery.removeEventListener(
      "change",
      this._setCalculatedTheme,
    );
  }

  private fillZapTemplateResults(suggestions: ZapSuggestion[]) {
    this.suggestions = suggestions;
    this.loadingSuggestions = false;
  }

  private async fetchDefaultZapTemplates() {
    try {
      this.loadingSuggestions = true;
      this.secondApp = undefined;
      await this.fetchZapTemplates();
    } finally {
      this.loadingSuggestions = false;
    }
  }

  private onAppSelected = async ({
    detail: app,
  }: CustomEvent<App | undefined>) => {
    if (!app || !app.slug) {
      this.fetchDefaultZapTemplates();
      return;
    }
    this.secondApp = app;
    try {
      this.loadingSuggestions = true;
      const zapTemplates = await fetchZapTemplates(
        [this.appSlug, app.slug],
        this.utmsWithProvider,
        { hideSlugs: this.hiddenSlugs },
      );
      if (zapTemplates) {
        this.fillZapTemplateResults(zapTemplates.results);
      }
    } finally {
      this.loadingSuggestions = false;
    }
  };

  @Watch("templateIds")
  @Watch("appExclusions")
  @Watch("appCategories")
  @Watch("appSlug")
  @Watch("otherAppSlugs")
  async fetchZapTemplates() {
    let results: ZapSuggestion[] = [];
    const templateIds = this.getZapTemplateIds();
    if (templateIds.length > 0) {
      ({ results } = await fetchZapTemplatesByIds(
        templateIds,
        this.utmsWithProvider,
      ));
    } else if (
      !(this.firstApp && this.secondApp) ||
      this.suggestions.length === 0
    ) {
      ({ results } = await fetchZapTemplates(
        [this.appSlug, ...this.otherAppSlugs],
        this.utmsWithProvider,
        {
          hideSlugs: this.hiddenSlugs,
          categoriesSlugs: this.categoriesSlugs,
        },
      ));
    } else if (this.firstApp && this.secondApp) {
      ({ results } = await fetchZapTemplates(
        [this.firstApp.slug, this.secondApp.slug],
        this.utmsWithProvider,
        {
          hideSlugs: this.hiddenSlugs,
          categoriesSlugs: this.categoriesSlugs,
        },
      ));
    }
    this.fillZapTemplateResults(results);
  }

  @Watch("clientId")
  clientIdChanged() {
    this.load();
  }

  @Watch("internalApp")
  internalAppChanged() {
    this.load();
  }

  @Watch("templateIds")
  @Watch("appSlug")
  appChanged() {
    this.firstApp = undefined;
    this.secondApp = undefined;
    this.fetchDefaultZapTemplates();
  }

  private async loadApp() {
    const app = await fetchApp(this.appSlug);
    this.firstApp = app;
  }

  // WARNING: Do NOT use `await` inside component lifecycle methods. If an
  // awaited promise rejects during `componentWillLoad`, extremely bad things
  // will happen. It seemed to cause Stencil.js to permanently crash. This file
  // is built on top of some older code that uses lots of competing async calls
  // and mutable state, making it one big race condition. Please take some time
  // to clean it up eventually. Thanks!
  //
  // Sage Fennel (#team-partner-sharing) [2022-12-20]
  componentWillLoad() {
    this.firstApp = undefined;
    this.secondApp = undefined;
    try {
      this.isLoggedIn = localStorageGetWithFallback(
        "is_logged_in",
        () => false,
      );
    } catch (err) {
      Logger.error(err);
    }
    this.appThemeChanged();
    this.fetchDefaultZapTemplates();
    this.load();
  }

  private async load(): Promise<void> {
    if (this.internalApp) {
      const [first, ...rest] = parseCsvSlugs(this.internalApp);
      this.appSlug = first;
      this.otherAppSlugs = rest;
    }
    if (this.clientId) {
      const { slug, selectedAPI } = await fetchSlugAndSelectedAPI({
        clientID: this.clientId,
      });
      this.appSlug = slug;
      this.selectedAPI = selectedAPI;
    } else {
      // Angular likes to hold off on setting the client ID for a frame or two,
      // so let's use setTimeout before erroring since in a lot of cases the
      // clientId is set shortly after mounting
      setTimeout(() => {
        if (!(this.clientId || this.appSlug || this.internalApp)) {
          Logger.error("missing `clientId`");
        }
      }, 1000);
    }
    if (this.appSlug) {
      await this.loadApp();
    }
    sendEmbedViewEvent({
      clientId: this.clientId,
      partnerName: this.appSlug,
      eventName: eventNames.workflowElementLoad,
      referrer: this.referrer,
      embedConfiguration: this.getEmbedConfiguration(),
      utmObject: this.getUtm(),
    });
  }

  // It would be great if we could use introspection to get this list of
  // properties... unfortunately I don't see any way to do that via Stencil.
  // That being said, we need to redact some of this information anyway, so it's
  // probably best to just hardcode it.
  //
  // Sage Fennel (2023-09-07)
  private getEmbedConfiguration(): EmbedViewEvent["embed_configuration"] {
    return formatEmbedConfiguration({
      clientId: this.clientId,
      theme: this.theme,
      introCopyDisplay: this.introCopyDisplay,
      appSelectionDisplay: this.appSelectionDisplay,
      appSearchBarDisplay: this.appSearchBarDisplay,
      appLimit: this.appLimit,
      appExclusions: this.appExclusions,
      appCategories: this.appCategories,
      zapLimit: this.templateLimit,
      zapCallToActionDisplay: this.templateCtaDisplay,
      zapCreateFromScratchDisplay: this.zapCreateFromScratchDisplay,
    });
  }

  @Watch("appSlug")
  onAppSlugPropChanged() {
    this.loadApp();
  }

  private shouldShowIntroCopy() {
    switch (this.internalAutoUpgradedFrom) {
      case "zte":
      case "zme": {
        return false;
      }
      case "ade":
      default: {
        return this.introCopyDisplay === "show";
      }
    }
  }

  private shouldShowAppSelection() {
    switch (this.internalAutoUpgradedFrom) {
      case "zte":
      case "zme": {
        return false;
      }
      default: {
        return this.appSlug && this.appSelectionDisplay !== "hide";
      }
    }
  }

  private shouldShowZapGuesser() {
    switch (this.internalAutoUpgradedFrom) {
      case "zte":
      case "zme": {
        return false;
      }
      default: {
        return this.clientId && this.guessZapDisplay === "show";
      }
    }
  }

  private shouldShowZapTemplates() {
    switch (this.internalAutoUpgradedFrom) {
      case "zme": {
        return false;
      }
      default: {
        return this.templateLimit !== 0;
      }
    }
  }

  private shouldShowZapTemplatesHeader() {
    switch (this.internalAutoUpgradedFrom) {
      case "zte": {
        return false;
      }
      default: {
        return true;
      }
    }
  }

  private shouldShowMyZaps() {
    switch (this.internalAutoUpgradedFrom) {
      case "ade":
      case "zte": {
        return false;
      }
      default: {
        return (
          this.clientId &&
          this.appSlug &&
          this.selectedAPI &&
          this.manageZapsDisplay === "show"
        );
      }
    }
  }

  // Apparently we don't want to show the "create from scratch" footer when
  // specific Zap Template IDs are supplied rather than using the "most popular"
  // Zaps related to given apps. I'm not sure why we landed on that decision,
  // though.
  private shouldShowCreateFromScratch() {
    if (this.templateIds) {
      return false;
    }
    return this.zapCreateFromScratchDisplay === "show";
  }

  private get referrer() {
    return referrerWithUTMs(location.href, this.getUtm());
  }

  private get quacInfo(): QuacInfo {
    return {
      clientId: this.clientId,
      signUpFirstName: this.signUpFirstName,
      signUpLastName: this.signUpLastName,
      signUpEmail: this.signUpEmail,
    };
  }

  private get hiddenSlugs() {
    return parseCsvSlugs(this.appExclusions);
  }

  private get categoriesSlugs() {
    return parseCsvSlugs(this.appCategories);
  }

  private getZapTemplateIds() {
    return parseCsvSlugs(this.templateIds);
  }

  private get utmsWithProvider() {
    return {
      ...this.getUtm(),
      provider: this.appSlug, // used for account provisioning
    };
  }

  private get _computedEmbedMode(): WFE.EmbedMode {
    switch (this.embedMode) {
      case "iframe":
      case "popup": {
        return this.embedMode;
      }
      default: {
        // If we're in an iframe, we need to check to see if there is the clientID is present,
        // because if so it's on the partner to correctly allowlist the domains they are trying to
        // nest in, but if not we should use a popup
        if (window.top !== window.self) {
          return this.clientId !== undefined ? "iframe" : "popup";
        }
        // QuAC is broken in Safari when clicking a Zap Template, so let's force
        // the use of popup mode
        if (browser === "safari" && hasValidQuacParams(this.quacInfo).isValid) {
          return "popup";
        }
        return "iframe";
      }
    }
  }

  private async handleInternalModalOpen(event: CustomEvent<ModalOpen>) {
    let iframeURL = event.detail.url;
    if (this.clientId != null) {
      try {
        const parsedURL = new URL(iframeURL);
        parsedURL.searchParams.set("client_id", this.clientId);
        iframeURL = parsedURL.href;
      } catch (err) {
        Logger.warn(
          `Failed to parse iframe URL, this may result in incorrect behavior: ${err}`,
        );
      }
    }
    switch (this._computedEmbedMode) {
      case "iframe": {
        this.modalState = {
          type: "open",
          url: iframeURL,
        };
        break;
      }
      case "popup": {
        const width = 1440;
        const height = 1440;
        const popup = window.open(
          event.detail.url,
          "zapier.editor-popup",
          `width=${width},height=${height}`,
        );

        if (!popup) {
          alert("Failed to open popup");
          return;
        }

        this.modalState = {
          type: "popup",
          url: event.detail.url,
          popup,
        };

        await waitForPopupClose(popup);
        this.modalState = { type: "closed" };

        // TODO: This might be removed once the other tasks are done
        if (this.manageZapsElement) {
          this.manageZapsElement.internalRefreshZaps();
        }
        break;
      }
      default: {
        assertNeverSoftly(this._computedEmbedMode);
        break;
      }
    }
  }

  private handleMessageToPopup(event: CustomEvent) {
    if (this.modalState.type === "popup" && this.modalState.popup) {
      this.modalState.popup.postMessage(event.detail, zapierURL);
    }
  }

  private hasAppSelection(): boolean {
    return Boolean(this.appSelectionDisplay === "show" && this.appSlug);
  }

  private hasAppSearchBar(): boolean {
    return Boolean(this.appSearchBarDisplay === "show");
  }

  private hasManageZaps(): boolean {
    return Boolean(this.manageZapsDisplay === "show" && this.clientId);
  }

  private async submitZapGuess() {
    this.zapGuesserError = undefined;
    const abortController = new AbortController();
    try {
      if (this.zapGuesserAbortController) {
        this.zapGuesserAbortController.abort();
      }
      this.zapGuesserIsLoading = true;
      this.zapGuesserAbortController = abortController;
      const data = await guessZap({
        clientId: this.clientId,
        description: this.zapGuesserPrompt,
        abortSignal: this.zapGuesserAbortController.signal,
      });
      this.zapGuesserResult = data;
    } catch (err) {
      if (!abortController.signal.aborted) {
        this.zapGuesserError = err as Error;
        throw err;
      }
    } finally {
      this.zapGuesserIsLoading = false;
    }
  }

  /**
   * Choose UTM values that reveal the underlying element is the Workflow
   * Element (WFE), but add a suffix showing which traditional element it _looks
   * like_ with its current configuration (ADE/ZTE/ZME/FZE).
   */
  // NOT private so that we can use it from a unit test
  getUtm(): UTMObject {
    const search = this.hasAppSearchBar();
    const select = this.hasAppSelection();
    const manage = this.hasManageZaps();
    if (search && select && manage) {
      return createUTMObject({ utm_campaign: "wfe_fze" });
    }
    if (!select && !search && !manage) {
      return createUTMObject({ utm_campaign: "wfe_zte" });
    }
    if (select && search && !manage) {
      return createUTMObject({ utm_campaign: "wfe_ade" });
    }
    if (!select && !search && manage) {
      return createUTMObject({ utm_campaign: "wfe_zme" });
    }
    return createUTMObject({ utm_campaign: "wfe_custom" });
  }

  render() {
    const query = new URLSearchParams(Object.entries(this.getUtm()));

    const createZapLink = `${zapierURL}/webintent/create-zap/?${query}`;

    const showRealIframe = this.modalState.type === "open" && !this.isDemoMode;
    const showDemoIframe = this.modalState.type === "open" && this.isDemoMode;
    const iframeURL =
      this.modalState.type === "open" && showRealIframe
        ? this.modalState.url
        : undefined;

    const zapTemplateIds = this.getZapTemplateIds();
    const showSpecificZapTemplates = zapTemplateIds.length > 0;
    const zapLimit = showSpecificZapTemplates
      ? zapTemplateIds.length
      : this.templateLimit;

    const key = `fze-zme-${this.clientId}`;
    return (
      <Host {...rootElementMetadata} data-theme={this.themeState}>
        {this.shouldShowIntroCopy() && (
          <zapier-intro-copy app-title={this.firstApp?.name} />
        )}

        {this.shouldShowMyZaps() && (
          <zapier-internal-manage-zaps
            // Regenerate the entire element from scratch when the client
            // ID changes, so the ZME is guaranteed to get the new client
            // ID
            key={key}
            id={key}
            onInternalLoggedIn={() => {
              this.isLoggedIn = true;
            }}
            onInternalLoggedOut={() => {
              this.isLoggedIn = false;
            }}
            onInternalModalOpen={this.handleInternalModalOpen.bind(this)}
            onInternalSendActionToPopup={this.handleMessageToPopup.bind(this)}
            internalModalState={this.modalState}
            internalUtm={this.getUtm()}
            ref={(zme) => {
              this.manageZapsElement = zme;
            }}
            internalSlug={this.appSlug}
            internalSelectedAPI={this.selectedAPI}
            internalFromWorkflowElement={true}
            clientId={this.clientId}
            embedMode={this._computedEmbedMode}
            signUpFirstName={this.signUpFirstName}
            signUpLastName={this.signUpLastName}
            signUpEmail={this.signUpEmail}
            theme={this.themeState}
            linkTarget="modal"
            data-internal-disable-aac={this.internalDisableAAC}
            data-internal-demo-mode={this.isDemoMode}
            data-internal-sdk-hostname={this.elem.dataset.internalSdkHostname}
            data-internal-popup-blocker={this.elem.dataset.internalPopupBlocker}
            data-internal-test-iframe-blocked={this.internalTestIframeBlocked}
          />
        )}

        {this.shouldShowZapGuesser() && (
          <form
            class="zap-guesser"
            onSubmit={(event) => {
              event.preventDefault();
              this.submitZapGuess();
            }}
          >
            <h2 id="zap-guesser--heading" class="zap-guesser--heading">
              What would you like to automate?
              <span class="zap-guesser--badge">
                <EvergreenAiIcon size="1em" /> AI beta
              </span>
            </h2>
            <div class="zap-guesser--search">
              <input
                class="input zap-guesser--input"
                type="text"
                maxlength={400}
                required
                placeholder="The more details, the better."
                value={this.zapGuesserPrompt}
                aria-labelledby="zap-guesser--heading zap-guesser--help-text"
                onChange={(event) => {
                  if (event.target instanceof HTMLInputElement) {
                    this.zapGuesserPrompt = event.target.value;
                  }
                }}
              />
              <button class="button zap-guesser-button" type="submit">
                Generate
              </button>
            </div>
            <div class="zap-guesser--help-text" id="zap-guesser--help-text">
              Example: When I add a reaction to a Slack message, create a card
              in Trello.
            </div>
            <output
              class="zap-guesser--output"
              hidden={
                !(
                  this.zapGuesserIsLoading ||
                  this.zapGuesserResult ||
                  this.zapGuesserError
                )
              }
            >
              {this.zapGuesserIsLoading ? (
                <span aria-label="Loading..." class="spinner" role="status" />
              ) : this.zapGuesserError ? (
                <span class="zap-guesser--error">
                  <WarningIcon /> Failed to generate workflow. Please try again.
                </span>
              ) : this.zapGuesserResult ? (
                <zapier-prefab-row
                  quacInfo={this.quacInfo}
                  referrer={this.referrer}
                  linkTarget="modal"
                  url={this.zapGuesserResult.prefilledUrl}
                  zapTitle={this.zapGuesserResult.title}
                  accessibleImages={this.zapGuesserResult.steps.map((s) => {
                    return {
                      src: getImageUrlForSelectedApi({
                        selectedApi: s.selectedApi,
                        size: 32,
                      }),
                      alt: s.appName,
                    };
                  })}
                  onModalOpen={(event) => {
                    event.stopPropagation();
                    this.handleInternalModalOpen(event);
                  }}
                  callToAction={
                    this.templateCtaDisplay === "hide"
                      ? undefined
                      : "Use this workflow"
                  }
                >
                  <div slot="footer-start">
                    <span class="zap-footer">
                      {this.zapGuesserResult.steps
                        .map((s) => s.appName)
                        .join(" + ")}
                    </span>
                  </div>
                </zapier-prefab-row>
              ) : null}
            </output>
          </form>
        )}

        {this.shouldShowAppSelection() && (
          <Fragment>
            {this.appSearchBarDisplay !== "hide" && (
              <div class="app-search">
                <zapier-app-select
                  onAppSelected={this.onAppSelected}
                  data-theme={this.themeState}
                  hide={this.appExclusions}
                  firstApp={this.firstApp}
                  secondApp={this.secondApp}
                />
              </div>
            )}
            {this.appLimit > 0 && (
              <zapier-app-suggestions
                onAppSelected={this.onAppSelected}
                data-theme={this.themeState}
                hide={this.appExclusions}
                categories={this.appCategories}
                client={this.appSlug}
                limit={this.appLimit}
              />
            )}
          </Fragment>
        )}

        {this.shouldShowZapTemplates() ? (
          <Fragment>
            {this.shouldShowZapTemplatesHeader() ? (
              <Fragment>
                {showSpecificZapTemplates ? (
                  <h2>Workflows</h2>
                ) : (
                  <h2>Popular workflows</h2>
                )}
              </Fragment>
            ) : null}
            <zapier-zap-suggestions
              facebookEmbed={this.internalFacebookEmbed}
              quacInfo={this.quacInfo}
              referrer={this.referrer}
              data-theme={this.themeState}
              loadingSuggestions={this.loadingSuggestions}
              suggestions={this.suggestions}
              limit={zapLimit}
              firstApp={this.firstApp}
              secondApp={this.secondApp}
              linkTarget="modal"
              onModalOpen={this.handleInternalModalOpen.bind(this)}
              callToAction={
                this.templateCtaDisplay === "hide"
                  ? undefined
                  : "Use this workflow"
              }
            />
          </Fragment>
        ) : null}

        <zapier-internal-modal
          size="large"
          // Why not just conditionally render the entire modal and iframe?
          // Well, I was having some weird issues with Stencil rendering the
          // iframe outside of the proper parent element. So I changed it.
          // Weird.
          isOpen={this.modalState.type === "open"}
        >
          <div class="modal-content">
            <button
              aria-label="Close"
              class="modal-button"
              type="button"
              onClick={(event) => {
                event.preventDefault();
                this.modalState = { type: "closed" };
                if (this.manageZapsElement) {
                  this.manageZapsElement.internalRefreshZaps();
                }
              }}
            >
              <CloseIcon />
            </button>
            <div class="iframe" hidden={!showDemoIframe}>
              <ImageZapEmptyState />
              <span>Users can view and edit their workflows here.</span>
            </div>
            <iframe
              referrerPolicy="origin"
              title="Create or edit your workflow"
              class="iframe"
              hidden={!showRealIframe}
              src={iframeURL}
              ref={(iframe) => {
                if (!iframe?.contentDocument) {
                  return;
                }
                // Add a lightly styled "Loading..." page so users know we're
                // working on loading their Zap. Otherwise opening a fully blank
                // window can be kinda weird looking if the Editor takes a bit
                // to load.
                iframe.contentDocument.body.innerHTML = html`
                  <style>
                    html {
                      padding: 0;
                      margin: 0;
                      font-family: sans-serif;
                      font-size: 1.5rem;
                      background: #fffdf9;
                      color: #2d2e2e;
                    }
                    body {
                      padding: 0;
                      margin: 0;
                      min-height: 100vh;
                      text-align: center;
                      display: flex;
                      justify-content: center;
                      align-items: center;
                    }
                  </style>
                  <div>Loading&hellip;</div>
                `;
              }}
            />
          </div>
        </zapier-internal-modal>

        {this.shouldShowCreateFromScratch() && (
          <div class="scratch">
            <p>Not seeing what you&apos;re looking for?</p>
            <a
              aria-label="Create a workflow from scratch"
              href={createZapLink}
              onClick={(event) => {
                event.preventDefault();
                this.modalState = {
                  type: "open",
                  url: createZapLink,
                };
              }}
              target={getAnchorTarget("modal")}
            >
              Create from scratch
            </a>{" "}
            or{" "}
            <a
              aria-label="Learn more about Zapier"
              href={`${zapierURL}/how-it-works/?${query}`}
            >
              learn more
            </a>
          </div>
        )}
      </Host>
    );
  }
}
